import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import JobForm from "@/components/JobForm";
import NotesPanel from "@/components/NotesPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, X, Send, Mic, Square, Loader2, Volume2, MessageSquare, Plus, Keyboard, Trash2, StickyNote, ShieldCheck, Lock, Database, Cpu, AlertTriangle, BarChart3 } from "lucide-react";

const GREETING = { role: "assistant", text: "Hi! I'm your Air Log assistant. Ask me about jobs, pilots, assets, parts or emails — or switch to New Job to add one by voice or text." };

export default function AiAssistant({ open, onOpenChange }) {
  const [mode, setMode] = useState("chat"); // chat | job
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const bottomRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  // job-entry state
  const [jobStage, setJobStage] = useState("idle"); // idle | recording | processing | review
  const [transcript, setTranscript] = useState("");
  const [parsed, setParsed] = useState(null);
  const [typedMode, setTypedMode] = useState(false);
  const [typedText, setTypedText] = useState("");
  const [policyOpen, setPolicyOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const openUsage = () => {
    setUsageOpen(true); setUsage(null);
    api.get("/ai/usage").then((r) => setUsage(r.data)).catch(() => setUsage({ error: true }));
  };
  const jobMediaRef = useRef(null);
  const jobChunksRef = useRef([]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const resetJob = () => { setJobStage("idle"); setTranscript(""); setParsed(null); setTypedMode(false); setTypedText(""); };

  const send = async (text) => {
    if (!text.trim()) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await api.post("/assistant/chat", { message: text });
      setMessages((m) => [...m, { role: "assistant", text: res.data.answer }]);
      if (res.data.created) { toast.success("Job added"); window.dispatchEvent(new Event("jobs:refresh")); }
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I hit an error. Please try again." }]);
    } finally { setLoading(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setLoading(true);
        const fd = new FormData(); fd.append("file", blob, "q.webm");
        try {
          const res = await api.post("/assistant/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setMessages((m) => [...m, { role: "user", text: res.data.transcript }, { role: "assistant", text: res.data.answer, audio: res.data.audio_base64 }]);
          if (res.data.created) { toast.success("Job added"); window.dispatchEvent(new Event("jobs:refresh")); }
          if (res.data.audio_base64 && audioRef.current) {
            audioRef.current.src = `data:audio/mpeg;base64,${res.data.audio_base64}`;
            audioRef.current.play().catch(() => {});
          }
        } catch {
          setMessages((m) => [...m, { role: "assistant", text: "Could not process the audio." }]);
        } finally { setLoading(false); }
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch (e) { console.error("Microphone access failed:", e); }
  };
  const stopRec = () => { if (mediaRef.current?.state !== "inactive") { mediaRef.current.stop(); setRecording(false); } };

  const playAudio = (b64) => { if (audioRef.current) { audioRef.current.src = `data:audio/mpeg;base64,${b64}`; audioRef.current.play(); } };

  // ---- job entry (voice / text) ----
  const startJobRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      jobChunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && jobChunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(jobChunksRef.current, { type: "audio/webm" });
        setJobStage("processing");
        const fd = new FormData(); fd.append("file", blob, "voice.webm");
        try {
          const res = await api.post("/voice/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setTranscript(res.data.transcript); setParsed({ ...res.data.parsed, source: "voice" }); setJobStage("review");
        } catch { toast.error("Could not process audio. Try typing instead."); setJobStage("idle"); }
      };
      jobMediaRef.current = mr; mr.start(); setJobStage("recording");
    } catch { toast.error("Microphone access denied. Try typing instead."); setTypedMode(true); }
  };
  const stopJobRec = () => { if (jobMediaRef.current?.state !== "inactive") { jobMediaRef.current.stop(); setJobStage("processing"); } };

  const sendJobTyped = async () => {
    if (!typedText.trim()) return;
    setJobStage("processing");
    try {
      const res = await api.post("/voice/parse-text", { text: typedText });
      setTranscript(res.data.transcript); setParsed({ ...res.data.parsed, source: "voice" }); setJobStage("review");
    } catch { toast.error("Could not parse text."); setJobStage("idle"); }
  };

  const switchMode = (m) => { setMode(m); if (m === "job") resetJob(); };

  return (
    <>
      <audio ref={audioRef} className="hidden" />
      {open && (
        <div data-testid="ai-assistant-panel" className="fixed top-20 right-5 z-40 w-[calc(100vw-2.5rem)] sm:w-96 h-[560px] max-h-[82vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-lime-400" /><span className="font-heading font-semibold">Air Log AI</span></div>
            <div className="flex items-center gap-1">
              <button onClick={() => setPolicyOpen(true)} data-testid="ai-policy-link" title="AI Policy & Privacy"
                className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded hover:bg-slate-800">
                <ShieldCheck className="h-4 w-4" /> AI Policy
              </button>
              <button onClick={openUsage} data-testid="ai-usage-link" title="AI Usage & cost"
                className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded hover:bg-slate-800">
                <BarChart3 className="h-4 w-4" /> AI Usage
              </button>
              {mode === "chat" && messages.length > 1 && (
                <button onClick={() => setMessages([GREETING])} data-testid="ai-clear-btn" title="Clear conversation"
                  className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded hover:bg-slate-800">
                  <Trash2 className="h-4 w-4" /> Clear
                </button>
              )}
              <button onClick={() => onOpenChange(false)} data-testid="ai-close-btn" className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
          </div>

          {/* mode tabs */}
          <div className="flex bg-slate-100 p-1 gap-1 shrink-0">
            <button data-testid="ai-mode-chat" onClick={() => switchMode("chat")}
              className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-medium py-1.5 rounded-lg transition-colors ${mode === "chat" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <MessageSquare className="h-4 w-4" /> Ask
            </button>
            <button data-testid="ai-mode-job" onClick={() => switchMode("job")}
              className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-medium py-1.5 rounded-lg transition-colors ${mode === "job" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <Plus className="h-4 w-4" /> New Job
            </button>
            <button data-testid="ai-mode-note" onClick={() => switchMode("note")}
              className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-medium py-1.5 rounded-lg transition-colors ${mode === "note" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <StickyNote className="h-4 w-4" /> Note
            </button>
          </div>

          {mode === "chat" ? (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                {messages.map((m, i) => (
                  <div key={`${m.role}-${i}-${m.text.slice(0, 12)}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-line ${m.role === "user" ? "bg-lime-400 text-slate-900" : "bg-white border border-slate-200 text-slate-700"}`}>
                      {m.text}
                      {m.audio && <button onClick={() => playAudio(m.audio)} className="mt-1.5 flex items-center gap-1 text-xs text-lime-600"><Volume2 className="h-3.5 w-3.5" /> Play</button>}
                    </div>
                  </div>
                ))}
                {loading && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5"><Loader2 className="h-4 w-4 animate-spin text-lime-500" /></div></div>}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-slate-200 flex items-center gap-2">
                <button data-testid="ai-mic-btn" onClick={recording ? stopRec : startRec} disabled={loading}
                  className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${recording ? "bg-red-500 text-white voice-pulse" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                  {recording ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5" />}
                </button>
                <Input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send(input)} placeholder="Ask about your operations…" disabled={loading} />
                <Button data-testid="ai-send-btn" onClick={() => send(input)} disabled={loading || !input.trim()} className="bg-slate-900 hover:bg-slate-800 text-white h-10 w-10 p-0 shrink-0"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          ) : mode === "job" ? (
            <div className="flex-1 overflow-y-auto p-4 bg-slate-50" data-testid="ai-job-mode">
              {jobStage !== "review" ? (
                <div className="py-6 flex flex-col items-center text-center">
                  {!typedMode ? (
                    <>
                      <button data-testid="ai-job-mic-btn" onClick={jobStage === "recording" ? stopJobRec : startJobRec} disabled={jobStage === "processing"}
                        className={`h-24 w-24 rounded-full flex items-center justify-center transition-all ${jobStage === "recording" ? "bg-red-500 voice-pulse" : "bg-lime-400 hover:bg-lime-300 hover:-translate-y-1"}`}>
                        {jobStage === "processing" ? <Loader2 className="h-9 w-9 text-slate-900 animate-spin" /> : jobStage === "recording" ? <Square className="h-8 w-8 text-white" fill="white" /> : <Mic className="h-10 w-10 text-slate-900" />}
                      </button>
                      <p className="mt-5 font-heading font-medium text-slate-800">
                        {jobStage === "recording" ? "Listening… tap to stop" : jobStage === "processing" ? "Transcribing & extracting…" : "Tap to speak your job"}
                      </p>
                      <p className="text-sm text-slate-500 mt-1 max-w-xs">e.g. "Survey job for BHP next Tuesday 9am in Moranbah, assign Sara with the Matrice"</p>
                      {jobStage === "idle" && (
                        <button data-testid="ai-job-type-btn" onClick={() => setTypedMode(true)} className="mt-5 text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5"><Keyboard className="h-4 w-4" /> Type instead</button>
                      )}
                    </>
                  ) : (
                    <div className="w-full space-y-3">
                      <Textarea data-testid="ai-job-text-input" value={typedText} onChange={(e) => setTypedText(e.target.value)} rows={4} disabled={jobStage === "processing"}
                        placeholder='Describe the job… e.g. "Powerline inspection for Ergon tomorrow 10am in Bundaberg, Jake flying the Mavic 3"' />
                      <div className="flex gap-2">
                        <Button data-testid="ai-job-parse-btn" onClick={sendJobTyped} disabled={jobStage === "processing"} className="flex-1 bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold">
                          {jobStage === "processing" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Extract Job Details"}
                        </Button>
                        <Button variant="outline" onClick={() => setTypedMode(false)} disabled={jobStage === "processing"}><Mic className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg bg-white p-3 border border-slate-200">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Transcript</p>
                    <p className="text-sm text-slate-700 italic" data-testid="ai-job-transcript">"{transcript}"</p>
                  </div>
                  <p className="text-sm text-slate-600 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-lime-500" /> Review the details and save.</p>
                  <JobForm initial={parsed} onSaved={() => { resetJob(); toast.success("Job added"); }} onClose={resetJob} />
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 bg-slate-50" data-testid="ai-note-mode">
              <NotesPanel compact ephemeral />
            </div>
          )}
        </div>
      )}

      <Dialog open={policyOpen} onOpenChange={setPolicyOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ai-policy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <ShieldCheck className="h-5 w-5 text-lime-500" /> AI Policy & Privacy
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 text-sm text-slate-600">
            <p className="text-slate-500">
              Air Log uses AI to help you capture jobs and notes faster, transcribe voice, and answer
              questions about your operations. Here's exactly what's used and how your data is handled.
            </p>

            <div>
              <h4 className="flex items-center gap-2 font-semibold text-slate-800 mb-1.5"><Cpu className="h-4 w-4 text-lime-500" /> Which AI models we use</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li><span className="font-medium text-slate-700">OpenAI Whisper</span> — converts your voice recordings into text for jobs and notes.</li>
                <li><span className="font-medium text-slate-700">OpenAI GPT-4o</span> — understands your text/voice to extract job details and answer questions.</li>
              </ul>
            </div>

            <div>
              <h4 className="flex items-center gap-2 font-semibold text-slate-800 mb-1.5"><Database className="h-4 w-4 text-lime-500" /> What data is sent</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Only the text or audio you actively dictate, type, or ask is sent to the AI provider.</li>
                <li>Relevant operational context (e.g. job, pilot, asset names) may be included so answers are accurate.</li>
                <li>Your recordings are transcribed and are not retained by Air Log after processing.</li>
              </ul>
            </div>

            <div>
              <h4 className="flex items-center gap-2 font-semibold text-slate-800 mb-1.5"><Lock className="h-4 w-4 text-lime-500" /> Privacy & security</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Requests are transmitted over encrypted (HTTPS) connections.</li>
                <li>AI providers do not use your Air Log data to train their models.</li>
                <li>Your data stays scoped to your organisation and is never shared with other customers.</li>
              </ul>
            </div>

            <div>
              <h4 className="flex items-center gap-2 font-semibold text-slate-800 mb-1.5"><AlertTriangle className="h-4 w-4 text-amber-500" /> Things to keep in mind</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>AI can make mistakes — always review extracted job details before saving.</li>
                <li>Avoid dictating sensitive personal information you don't want processed by a third party.</li>
                <li>AI output is guidance only and does not replace CASA regulatory or safety obligations.</li>
              </ul>
            </div>

            <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
              Questions about how your data is handled? Contact your Air Log administrator.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={usageOpen} onOpenChange={setUsageOpen}>
        <DialogContent className="max-w-md" data-testid="ai-usage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <BarChart3 className="h-5 w-5 text-lime-500" /> AI Usage & Cost
            </DialogTitle>
          </DialogHeader>
          {!usage ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading usage…</div>
          ) : usage.error ? (
            <p className="text-sm text-amber-700 py-4">Could not load usage data.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Total tokens</p>
                  <p className="text-xl font-bold text-slate-900 font-heading" data-testid="ai-usage-tokens">{usage.total_tokens.toLocaleString()}</p>
                  <p className="text-[11px] text-slate-400">{usage.total_input_tokens.toLocaleString()} in · {usage.total_output_tokens.toLocaleString()} out</p>
                </div>
                <div className="rounded-lg border border-lime-200 bg-lime-50 px-3 py-2.5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-lime-700">Est. cost</p>
                  <p className="text-xl font-bold text-slate-900 font-heading" data-testid="ai-usage-cost">${usage.total_cost_usd.toFixed(4)}</p>
                  <p className="text-[11px] text-slate-400">{usage.total_calls.toLocaleString()} AI calls</p>
                </div>
              </div>
              {usage.by_kind?.length > 0 && (
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="grid grid-cols-4 gap-2 px-3 py-2 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    <span className="col-span-2">Feature</span><span className="text-right">Tokens</span><span className="text-right">Cost</span>
                  </div>
                  {usage.by_kind.map((k) => (
                    <div key={k.kind} className="grid grid-cols-4 gap-2 px-3 py-2 border-t border-slate-100 text-sm" data-testid={`ai-usage-kind-${k.kind}`}>
                      <span className="col-span-2 text-slate-700 capitalize">{k.kind.replace(/_/g, " ")} <span className="text-slate-400 text-xs">×{k.calls}</span></span>
                      <span className="text-right text-slate-600">{k.tokens.toLocaleString()}</span>
                      <span className="text-right text-slate-900 font-medium">${k.cost_usd.toFixed(4)}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-400">Costs are estimated from token counts using current OpenAI rates (GPT-5.5, GPT-5.4, Whisper & TTS). Actual billing is drawn from your Emergent key balance.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
